import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Input } from '../../ui/input';
import { Segmented } from './Segmented'; // Assuming you have a segmented control component
import { useStrategySchema } from '../../../context/StrategySchemaContext';
import TakeProfitLadder from './TakeProfitLadder';

// Import legacy risk schema (non-ladder fields)
import { riskSchema } from './riskSchema';

// Safe ID generator (fallback when crypto.randomUUID is unavailable)
const genId = () => {
  try {
    if (typeof crypto !== 'undefined') {
      if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
      if (crypto.getRandomValues) {
        const buf = new Uint8Array(16);
        crypto.getRandomValues(buf);
        // RFC4122-ish formatting
        buf[6] = (buf[6] & 0x0f) | 0x40; // version 4
        buf[8] = (buf[8] & 0x3f) | 0x80; // variant
        const hex = [...buf].map(b => b.toString(16).padStart(2, '0'));
        return (
          hex.slice(0, 4).join('') + '-' +
          hex.slice(4, 6).join('') + '-' +
          hex.slice(6, 8).join('') + '-' +
          hex.slice(8, 10).join('') + '-' +
          hex.slice(10, 16).join('')
        );
      }
    }
  } catch { /* ignore */ }
  return 'id-' + Math.random().toString(36).slice(2, 11);
};

const RiskForm = ({ value, onChange }) => {
  // Rename to avoid clashing with exported Zod schema 'riskSchema'
  const { riskSchema: riskJsonSchema, capabilities } = useStrategySchema();
  const multiTpEnabled = capabilities?.multi_tp?.enabled !== false; // default true if unspecified
  const tpTargetsSchema = riskJsonSchema?.properties?.tp_targets;
  const ladderAvailable = multiTpEnabled && !!tpTargetsSchema;
  // Local ladder state with internal IDs
  const [tpTargets, setTpTargets] = useState(() => value?.tp_targets?.map(r => ({ ...r, localId: genId() })) || []);

  const { register, handleSubmit, formState: { errors }, watch, setValue, reset } = useForm({
    resolver: zodResolver(riskSchema),
    defaultValues: value,
  });

  // Refs to avoid redundant parent updates & detect external hydration
  const lastPushedJsonRef = useRef('');
  const initializingRef = useRef(true);

  const slType = watch('sl.type');

  // Watch individual primitive fields to limit re-renders
  const wRiskPct = watch('risk_pct');
  const wSlType = watch('sl.type');
  const wSlMult = watch('sl.mult');
  const wSlValue = watch('sl.value');
  const wTpR = watch('tp.r');
  const wDefaultRR = watch('default_rr');
  const wMaxOpen = watch('max_open_positions');
  const wDailyLoss = watch('daily_loss_pct');
  const wCooldown = watch('cooldown_minutes');
  const wConsecutiveLoss = watch('consecutive_loss_limit');
  const wMaxTradeRisk = watch('max_trade_risk');
  const wMaxLotSize = watch('max_lot_size');

  // Helper to build normalized risk object
  const buildRisk = () => {
    const sl = wSlType === 'atr'
      ? { type: 'atr', mult: Number(wSlMult) || 0 }
      : { type: 'pct', value: Number(wSlValue) || 0 };
    const risk = {
      // Keep UI/state in PERCENT units (e.g., user enters 0.35 meaning 0.35%).
      // Backend expects FRACTION (0.0035), which is handled centrally in serialize.js.
      risk_pct: (() => {
        const raw = Number(wRiskPct);
        return Number.isFinite(raw) ? raw : 0;
      })(),
      sl,
      tp: { type: 'r_multiple', r: Number(wTpR) || 0 },
      default_rr: Number(wDefaultRR) || 0,
      max_open_positions: Number(wMaxOpen) || 0,
      daily_loss_pct: Number(wDailyLoss) || 0,
      cooldown_minutes: Number(wCooldown) || 0,
  // Optional advanced limits
  ...(wConsecutiveLoss != null && wConsecutiveLoss !== '' ? { consecutive_loss_limit: Number(wConsecutiveLoss) } : {}),
  ...(wMaxTradeRisk != null && wMaxTradeRisk !== '' ? { max_trade_risk: Number(wMaxTradeRisk) } : {}),
  ...(wMaxLotSize != null && wMaxLotSize !== '' ? { max_lot_size: Number(wMaxLotSize) } : {}),
    };
    if (ladderAvailable && tpTargets.length) {
      risk.tp_targets = tpTargets.map(t => {
        const copy = { ...t };
        delete copy.localId;
        return copy;
      });
    }
    return risk;
  };

  const hashRisk = (r) => {
    // Deterministic ordering
    return JSON.stringify({
      risk_pct: r.risk_pct,
      sl: r.sl,
      tp: r.tp,
      default_rr: r.default_rr,
      max_open_positions: r.max_open_positions,
      daily_loss_pct: r.daily_loss_pct,
      cooldown_minutes: r.cooldown_minutes,
  tp_targets: r.tp_targets || null,
  consecutive_loss_limit: r.consecutive_loss_limit ?? null,
  max_trade_risk: r.max_trade_risk ?? null,
  max_lot_size: r.max_lot_size ?? null,
    });
  };

  // External hydration detector — state/value is already in PERCENT units; no extra scaling.
  useEffect(() => {
    if (initializingRef.current) {
      initializingRef.current = false;
    }
    const source = value ? { ...value } : value;
    const display = source ? { ...source } : source;
    const parentHash = hashRisk({ ...source });
    if (parentHash !== lastPushedJsonRef.current) {
      reset(display);
      if (ladderAvailable) {
        const incoming = Array.isArray(source?.tp_targets) ? source.tp_targets : [];
        setTpTargets(incoming.map(r => ({ ...r, localId: genId() })));
      }
      lastPushedJsonRef.current = parentHash;
    }
  }, [value, ladderAvailable, reset]);

  // Push local edits up only when hash changes AND differs from parent snapshot
  useEffect(() => {
    const built = buildRisk();
    const h = hashRisk(built);
    if (h !== lastPushedJsonRef.current) {
      lastPushedJsonRef.current = h;
      onChange(built);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wRiskPct, wSlType, wSlMult, wSlValue, wTpR, wDefaultRR, wMaxOpen, wDailyLoss, wCooldown, tpTargets, ladderAvailable]);
  // Include new fields in change propagation deps
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { /* no-op: dependency list extension */ }, [wConsecutiveLoss, wMaxTradeRisk, wMaxLotSize]);

  const slConfigured = useMemo(() => {
    const sl = watch('sl');
    if (!sl) return false;
    if (sl.type === 'atr') return sl.mult > 0;
    if (sl.type === 'pct') return sl.value > 0;
    return false;
  }, [watch]);

  return (
  <form onSubmit={handleSubmit(onChange)}>
      <h3 className="text-lg font-semibold mb-4">Risk Management</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Column 1 */}
        <div className="space-y-4">
          <div>
            <label htmlFor="risk_pct" className="block text-sm font-medium text-textSecondary">Risk per Trade (%)</label>
            <Input id="risk_pct" type="number" {...register('risk_pct', { valueAsNumber: true })} />
            {errors.risk_pct && <p className="text-destructive text-xs mt-1">{errors.risk_pct.message}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-textSecondary mb-1">Stop Loss Type</label>
            <Segmented
              value={slType}
              onChange={(newType) => setValue('sl.type', newType)}
              options={[
                { label: 'ATR', value: 'atr' },
                { label: 'Percent', value: 'pct' },
              ]}
            />
          </div>

          {slType === 'atr' && (
            <div>
              <label htmlFor="sl.mult" className="block text-sm font-medium text-textSecondary">SL ATR Multiplier</label>
              <Input id="sl.mult" type="number" {...register('sl.mult', { valueAsNumber: true })} />
              {errors.sl?.mult && <p className="text-destructive text-xs mt-1">{errors.sl.mult.message}</p>}
            </div>
          )}

          {slType === 'pct' && (
            <div>
              <label htmlFor="sl.value" className="block text-sm font-medium text-textSecondary">SL Percent</label>
              <Input id="sl.value" type="number" {...register('sl.value', { valueAsNumber: true })} />
              {errors.sl?.value && <p className="text-destructive text-xs mt-1">{errors.sl.value.message}</p>}
            </div>
          )}

          {!ladderAvailable && (
            <div>
              <label htmlFor="tp.r" className="block text-sm font-medium text-textSecondary">Take Profit (R-Multiple)</label>
              <Input id="tp.r" type="number" {...register('tp.r', { valueAsNumber: true })} />
              {errors.tp?.r && <p className="text-destructive text-xs mt-1">{errors.tp.r.message}</p>}
            </div>
          )}
          <div>
            <label htmlFor="default_rr" className="block text-sm font-medium text-textSecondary">Default RR Ratio</label>
            <Input id="default_rr" type="number" {...register('default_rr', { valueAsNumber: true })} />
            {errors.default_rr && <p className="text-destructive text-xs mt-1">{errors.default_rr.message}</p>}
          </div>
        </div>

        {/* Column 2 */}
  <div className="space-y-4">
          <div>
            <label htmlFor="max_open_positions" className="block text-sm font-medium text-textSecondary">Max Open Positions</label>
            <Input id="max_open_positions" type="number" {...register('max_open_positions', { valueAsNumber: true })} />
            {errors.max_open_positions && <p className="text-destructive text-xs mt-1">{errors.max_open_positions.message}</p>}
          </div>
          <div>
            <label htmlFor="daily_loss_pct" className="block text-sm font-medium text-textSecondary">Max Daily Loss (%)</label>
            <Input id="daily_loss_pct" type="number" {...register('daily_loss_pct', { valueAsNumber: true })} />
            {errors.daily_loss_pct && <p className="text-destructive text-xs mt-1">{errors.daily_loss_pct.message}</p>}
          </div>
          <div>
            <label htmlFor="cooldown_minutes" className="block text-sm font-medium text-textSecondary">Cooldown (Minutes)</label>
            <Input id="cooldown_minutes" type="number" {...register('cooldown_minutes', { valueAsNumber: true })} />
            {errors.cooldown_minutes && <p className="text-destructive text-xs mt-1">{errors.cooldown_minutes.message}</p>}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label htmlFor="consecutive_loss_limit" className="block text-sm font-medium text-textSecondary">Consecutive Loss Limit</label>
              <Input id="consecutive_loss_limit" type="number" {...register('consecutive_loss_limit', { valueAsNumber: true })} />
              {errors.consecutive_loss_limit && <p className="text-destructive text-xs mt-1">{errors.consecutive_loss_limit.message}</p>}
            </div>
            <div>
              <label htmlFor="max_trade_risk" className="block text-sm font-medium text-textSecondary">Max Trade Risk</label>
              <Input id="max_trade_risk" type="number" {...register('max_trade_risk', { valueAsNumber: true })} />
              {errors.max_trade_risk && <p className="text-destructive text-xs mt-1">{errors.max_trade_risk.message}</p>}
            </div>
            <div>
              <label htmlFor="max_lot_size" className="block text-sm font-medium text-textSecondary">Max Lot Size</label>
              <Input id="max_lot_size" type="number" {...register('max_lot_size', { valueAsNumber: true })} />
              {errors.max_lot_size && <p className="text-destructive text-xs mt-1">{errors.max_lot_size.message}</p>}
            </div>
          </div>
          {ladderAvailable && (
            <div className="pt-2 border-t border-borderDefault">
              <TakeProfitLadder value={tpTargets} onChange={setTpTargets} slConfigured={slConfigured} />
            </div>
          )}
        </div>
      </div>
    </form>
  );
};

export default RiskForm;
